import {
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable } from 'rxjs';
import { AuthService } from '../services/auth/auth.service';


/**
 * Authenticated mutations under `/api/auth/` that DO require the CSRF header.
 *
 * **Empty today.** The prefix is skipped wholesale below because almost
 * everything under it is anonymous (login, dev-login, forgot-password,
 * reset-password, valid-token — no session to protect, no token to send) or
 * server-side CSRF-exempt (`logout`). The list exists for the rare
 * authenticated mutation that must live there anyway; its only former entry,
 * `change-password`, was retired when the backend moved it to
 * `POST /api/user/change-password`.
 *
 * Getting this wrong is expensive to diagnose, because the backend's global
 * antiforgery filter answers a missing token with **401 "Invalid antiforgery
 * token"** rather than 403 — which reads exactly like an expired session.
 *
 * Add a path here only if an authenticated mutation genuinely has to live under
 * `/api/auth/`; prefer putting it on another controller (e.g. `/api/user/`).
 */
const CSRF_REQUIRED_AUTH_PATHS: readonly string[] = [];

export const csrfInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
  const authService = inject(AuthService);
  const url = new URL(req.url, location.origin);

  // Skip GET requests
  if (req.method === 'GET') {
    return next(req);
  }

  // Bearer-authed dev mutations (POST/PUT/PATCH/DELETE on /api/admin/dev/*
  // + future mutation endpoints) also need X-XSRF-TOKEN: ASP.NET Core's
  // antiforgery middleware validates the header against the XSRF cookie
  // regardless of which auth scheme populated the principal. The two
  // headers coexist — Bearer authenticates, CSRF validates the origin.
  const isSkippedAuthPath =
    url.pathname.startsWith('/api/auth/') &&
    !CSRF_REQUIRED_AUTH_PATHS.includes(url.pathname);

  if (!isSkippedAuthPath &&
      authService.isAuthenticated() && authService.csrfToken()) {
    const modifiedReq = req.clone({
      headers: req.headers.set('X-XSRF-TOKEN', authService.csrfToken() as string),
    });
    return next(modifiedReq);
  }

  return next(req);
};

